'use client';

import { useEffect, useRef } from 'react';

import {
  clearInterval as clearWorkerInterval,
  setInterval as setWorkerInterval,
} from 'worker-timers';

/**
 * Browsers throttle a hidden tab's timers to as little as one tick a minute, but leave a
 * worker's alone, so anything that has to keep time while the user is looking at
 * something else runs there instead. Where workers are unavailable — a server render, a
 * test renderer — the page's own timers stand in, which keeps time just as well when
 * nothing is being throttled.
 */
const canUseWorkerTimers = typeof Worker !== 'undefined';

/**
 * Runs `callback` every `delayInMs`, or not at all while the delay is `null`. Changing
 * the callback does not restart the interval, so a caller may pass a fresh closure on
 * every render without losing a tick.
 */
export function useUnthrottledInterval(
  callback: () => void,
  delayInMs: number | null,
) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delayInMs === null) {
      return;
    }

    const tick = () => savedCallback.current();

    if (!canUseWorkerTimers) {
      const timerId = setInterval(tick, delayInMs);

      return () => clearInterval(timerId);
    }

    const timerId = setWorkerInterval(tick, delayInMs);

    return () => clearWorkerInterval(timerId);
  }, [delayInMs]);
}
